// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Workspace administrators define the fields that knowledge-base
 * items and datasets use on this page. The route supplies the workspace heading
 * and shared setup map, while the schema catalog supplies the toolbar and
 * table. Keeping navigation context here lets the catalog remain reusable
 * without hiding where schemas fit in the analysis setup workflow.
 */
import type { FC } from 'react'
import { useAuth } from '@/subsystems/workspaces'
import { SchemaCatalogPanel } from '@/subsystems/schema-catalog'
import { ConfigFlowStrip } from '@/app-shell/components/ConfigFlowStrip'

const SchemasPage: FC = () => {
  const { isGlobalAdmin, selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const workspaceScope =
    !isGlobalAdmin && selectedOrganizationId && selectedWorkspaceId
      ? { orgId: selectedOrganizationId, wsId: selectedWorkspaceId }
      : undefined
  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-[1280px] px-6 pt-9 pb-16 sm:px-10">
        <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Schemas</h1>
        <p className="mt-[5px] text-[13px] text-slate-500">
          Schemas define the fields the AI fills in when it reviews a document — the shape of
          every extraction result.
        </p>
        {isGlobalAdmin && (
          <div className="mt-6">
            <ConfigFlowStrip current="schemas" />
          </div>
        )}
        <div className="mt-6">
          <SchemaCatalogPanel readonly={!isGlobalAdmin} workspaceScope={workspaceScope} />
        </div>
      </div>
    </div>
  )
}

export default SchemasPage
